import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRole } from 'features/auth/contexts/RoleContext';
import { GenericLoading } from 'components/ui/Loader';

interface RoleGuardProps {
  fallback?: string;
  fallbackComponent?: ReactNode;
  children: ReactNode;
}

/**
 * Guard qui vérifie si l'utilisateur a accès à la route actuelle
 * en fonction de son rôle courant.
 */
export const RoleGuard = ({
  // Default fallback: /bord is the only route every portal allows; /mes-leads
  // is not guarded and fails under a portal without the matching brick.
  fallback = '/bord',
  fallbackComponent,
  children
}: RoleGuardProps) => {
  const { isRouteAllowed, isLoading } = useRole();
  const location = useLocation();

  // Attendre que le rôle soit chargé
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <GenericLoading loading isParentSized />
          <p className="text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Vérifier si la route est autorisée pour le rôle courant
  const hasAccess = isRouteAllowed(location.pathname);

  if (!hasAccess) {
    return fallbackComponent ? (
      <>{fallbackComponent}</>
    ) : (
      <Navigate to={fallback} replace />
    );
  }

  return <>{children}</>;
};
